import { lazy, Suspense, useEffect } from "react";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
const ScrollProgressBar = lazy(() => import("@/components/ScrollProgressBar"));
import { PAGE_SEO } from "@/lib/seo-config";
import SEOHead from "@/components/SEOHead";
import {
  buildMedicalBusinessSchema,
  buildPhysicianSchema,
  buildFAQPageSchema,
  buildServiceSchemas,
  buildWebSiteSchema,
  buildOrganizationSchema,
  buildSiteNavigationSchema,
  buildHowToSchema,
} from "@/lib/schema-data";
import { trackViewContent } from "@/lib/tiktok-pixel";
import { useVerifiedTracking } from "@/hooks/use-verified-tracking";
import { useScrollDepth } from "@/hooks/use-scroll-depth";
import { LazySection } from "@/components/LazySection";
import { useLanguage } from "@/contexts/LanguageContext";

/* ── Lazy-loaded below-the-fold sections ── */

const HeroTestimonials = lazy(() => import("@/components/HeroTestimonials"));
const StatsSection = lazy(() => import("@/components/StatsSection"));
const PhilosophySection = lazy(() => import("@/components/PhilosophySection"));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const FoundationSessionSection = lazy(() => import("@/components/FoundationSessionSection"));
const ServicesSection = lazy(() => import("@/components/ServicesSection"));
const UrgencyBanner = lazy(() => import("@/components/UrgencyBanner"));
const TherapeuticNutritionSection = lazy(() => import("@/components/TherapeuticNutritionSection"));
const AuthorBlock = lazy(() => import("@/components/AuthorBlock"));
const FAQSection = lazy(() => import("@/components/FAQSection"));
const LatestArticlesSection = lazy(() => import("@/components/LatestArticlesSection"));
const LeadMagnet = lazy(() => import("@/components/LeadMagnet"));
const ContactSection = lazy(() => import("@/components/ContactSection"));
const Footer = lazy(() => import("@/components/Footer"));
const StickyMobileCTA = lazy(() => import("@/components/StickyMobileCTA"));
const WhatsAppButton = lazy(() => import("@/components/WhatsAppButton"));
const SocialProofToast = lazy(() => import("@/components/SocialProofToast"));
const ExitIntentPopup = lazy(() => import("@/components/ExitIntentPopup"));

const Index = () => {
  useVerifiedTracking();
  useScrollDepth();
  const { dir } = useLanguage();

  useEffect(() => {
    // Defer pixel ViewContent until after the browser is idle so it never
    // contends with the LCP paint of the hero image.
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (h: number) => void;
    };
    let idleHandle: number | undefined;
    let timeoutHandle: number | undefined;
    const fire = () => trackViewContent('home_page', 'الصفحة الرئيسية', 'تغذية علاجية');
    if (typeof w.requestIdleCallback === "function") {
      idleHandle = w.requestIdleCallback(fire, { timeout: 4000 });
    } else {
      timeoutHandle = window.setTimeout(fire, 2000);
    }
    return () => {
      if (idleHandle !== undefined && w.cancelIdleCallback) w.cancelIdleCallback(idleHandle);
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    };
  }, []);

  // Scroll to hash targets (e.g. /#foundation-session) — sections are lazy
  // loaded, so retry for a few seconds until the element mounts.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    const id = decodeURIComponent(hash.slice(1));
    let tries = 0;
    const timer = window.setInterval(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.clearInterval(timer);
      } else if (++tries > 40) {
        window.clearInterval(timer);
      }
    }, 150);
    return () => window.clearInterval(timer);
  }, []);



  return (
    <div className="min-h-screen bg-background overflow-x-hidden" dir={dir}>
      <SEOHead
        {...PAGE_SEO.home}
        ogLocale="ar_EG"
        alternateUrls={{
          "ar-EG": "https://www.drshimaaanwer.com/",
          "ar-SA": "https://www.drshimaaanwer.com/sa",
          "ar": "https://www.drshimaaanwer.com/",
        }}
        jsonLd={[
          buildMedicalBusinessSchema(),
          buildPhysicianSchema(),
          buildFAQPageSchema(),
          ...buildServiceSchemas(),
          buildWebSiteSchema(),
          buildOrganizationSchema(),
          buildSiteNavigationSchema(),
          buildHowToSchema(),
        ]}
      />
      <Suspense fallback={null}><ScrollProgressBar /></Suspense>
      <header>
        <Navbar />
      </header>
      <main id="main-content">
        {/* Above-the-fold: eagerly loaded, no Suspense */}
        <HeroSection />
        <Suspense fallback={null}><HeroTestimonials /></Suspense>
        {/* Below-the-fold: each section mounts only when near viewport */}

        <LazySection fallbackHeight="300px">
          <Suspense fallback={null}><StatsSection /></Suspense>
        </LazySection>

        <LazySection fallbackHeight="200px">
          <Suspense fallback={null}><UrgencyBanner /></Suspense>
        </LazySection>

        <LazySection fallbackHeight="600px">
          <Suspense fallback={null}><TherapeuticNutritionSection /></Suspense>
        </LazySection>

        <LazySection fallbackHeight="500px">
          <Suspense fallback={null}><PhilosophySection /></Suspense>
        </LazySection>

        <LazySection fallbackHeight="500px">
          <Suspense fallback={null}><TestimonialsSection /></Suspense>
        </LazySection>

        <LazySection fallbackHeight="600px">
          <Suspense fallback={null}><AuthorBlock /></Suspense>
        </LazySection>

        <LazySection fallbackHeight="400px">
          <Suspense fallback={null}><FoundationSessionSection /></Suspense>
        </LazySection>

        <LazySection fallbackHeight="700px">
          <Suspense fallback={null}><ServicesSection /></Suspense>
        </LazySection>

        <LazySection fallbackHeight="500px">
          <Suspense fallback={null}><FAQSection /></Suspense>
        </LazySection>

        <LazySection fallbackHeight="600px">
          <Suspense fallback={null}><LatestArticlesSection /></Suspense>
        </LazySection>

        <LazySection fallbackHeight="400px">
          <Suspense fallback={null}><LeadMagnet /></Suspense>
        </LazySection>

        <LazySection fallbackHeight="500px">
          <Suspense fallback={null}><ContactSection /></Suspense>
        </LazySection>
      </main>

      <Suspense fallback={null}>
        <StickyMobileCTA />
        <Footer />
        <WhatsAppButton />
        <SocialProofToast />
        <ExitIntentPopup />
      </Suspense>
    </div>
  );
};

export default Index;
